import { useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ChevronDown, Music, VolumeX } from "lucide-react";
import { StarfieldCanvas } from "./StarfieldCanvas";
import { startHarp, stopHarp, harpRunning } from "../audio/harp";
import { CEREMONY_DATE_LABEL, VENUE_NAME } from "../config";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const ref = useRef(null);
  const [audioOn, setAudioOn] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const moonY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const mX = useMotionValue(0);
  const mY = useMotionValue(0);
  const moonMX = useSpring(useTransform(mX, [-0.5, 0.5], [-20, 20]), { stiffness: 55, damping: 18 });
  const moonMY = useSpring(useTransform(mY, [-0.5, 0.5], [-12, 12]), { stiffness: 55, damping: 18 });
  const titleMX = useSpring(useTransform(mX, [-0.5, 0.5], [10, -10]), { stiffness: 55, damping: 18 });
  const onMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mX.set(e.clientX / r.width - 0.5);
    mY.set(e.clientY / r.height - 0.5);
  };

  const toggleAudio = () => {
    if (harpRunning()) {
      stopHarp();
      setAudioOn(false);
    } else {
      startHarp();
      setAudioOn(true);
    }
  };

  const scrollTo = (href) => {
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(href, { offset: -60, duration: 1.8 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" ref={ref} onMouseMove={onMouse} onMouseLeave={() => { mX.set(0); mY.set(0); }} className="relative min-h-[100svh] overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#070814] via-[#0d0f22] to-[#19102e]" />
      <StarfieldCanvas className="absolute inset-0 w-full h-full" />

      <motion.div style={{ y: moonY }} className="absolute top-[7%] right-[6%] sm:top-[10%] sm:right-[14%] w-20 h-20 sm:w-40 sm:h-40">
        <motion.div
          style={{ x: moonMX, y: moonMY }}
          className="w-full h-full rounded-full bg-[radial-gradient(circle_at_35%_35%,#fffbe8,#f3e5ab_45%,#c9a84c_80%,#8a6d2a)] shadow-[0_0_80px_30px_rgba(243,229,171,0.28)]"
        />
      </motion.div>

      <div className="cloud-drift absolute top-[22%] left-[-10%] w-[420px] h-24 bg-lavender-200/10 blur-3xl rounded-full" />
      <div className="cloud-drift-slow absolute top-[38%] right-[-12%] w-[520px] h-28 bg-rose-200/10 blur-3xl rounded-full" />

      <div className="mist absolute bottom-0 inset-x-0 h-[32vh] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {[10, 26, 43, 58, 73, 88].map((x, i) => (
          <div
            key={x}
            className="lantern-loop absolute bottom-[-8%]"
            style={{ left: `${x}%`, animationDelay: `${i * 1.6}s`, animationDuration: `${9 + (i % 3) * 2.5}s` }}
          >
            <div
              className="w-7 h-10 rounded-t-full rounded-b-lg"
              style={{
                background: "radial-gradient(circle at 50% 70%, #f3e5ab, rgba(243,229,171,0.5) 60%, transparent)",
                boxShadow: "0 0 22px 7px rgba(243,229,171,0.45)",
              }}
            />
          </div>
        ))}
      </div>

      <motion.div
        style={{ y: titleY, opacity: fade, x: titleMX }}
        className="relative z-10 min-h-[100svh] flex flex-col items-center justify-center text-center px-6 pt-28 sm:pt-32 pb-[8vh]"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.9, ease: EASE }}
          data-testid="hero-eyebrow"
          className="eyebrow mb-6 mt-2 sm:mt-4"
        >
          Under the starlight sky, a princess is named
        </motion.p>

        <h1 data-testid="hero-title-haya" className="font-royal leading-[0.95]">
          {["Haya", "Noor", "Jamal"].map((word, i) => (
            <span key={word} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.7 + i * 0.22, duration: 1, ease: EASE }}
                className="block text-5xl sm:text-7xl lg:text-8xl gold-text tracking-wide"
              >
                {word}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
          className="font-script text-2xl sm:text-3xl text-rose-200/90 mt-5"
        >
          her aqiqah <span className="text-amber-200/70">·</span> <span lang="ar">عقيقة</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2, duration: 0.8, ease: EASE }}
          data-testid="hero-date-badge"
          className="mt-8 glass-card rounded-full px-6 py-2.5 text-[0.7rem] sm:text-xs tracking-[0.3em] uppercase text-amber-100/90"
        >
          {CEREMONY_DATE_LABEL} &nbsp;·&nbsp; {VENUE_NAME}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.2, duration: 0.8, ease: EASE }}
          className="mt-9 flex flex-wrap items-center justify-center gap-4 pointer-events-auto"
        >
          <button data-testid="hero-rsvp-button" className="btn-gold" onClick={() => scrollTo("#invitation")}>
            RSVP via WhatsApp
          </button>
          <button data-testid="hero-story-button" className="btn-ghost" onClick={() => scrollTo("#chapters")}>
            Explore Her Story
          </button>
        </motion.div>

        <motion.a
          href="/journey"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.6, duration: 0.8, ease: EASE }}
          data-testid="hero-journey-link"
          className="mt-8 inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-amber-300/60 bg-amber-300/10 backdrop-blur-md text-[0.72rem] sm:text-xs tracking-[0.28em] uppercase text-amber-100 glow-pulse float-soft hover:bg-amber-300/20 hover:scale-105 hover:border-amber-300 transition-[background-color,border-color,transform] duration-300 pointer-events-auto"
        >
          ✦ step inside the immersive fairytale ✦
        </motion.a>
      </motion.div>

      <button
        data-testid="audio-toggle-button"
        onClick={toggleAudio}
        aria-label="Toggle fairy harp music"
        className="absolute z-20 bottom-24 sm:bottom-28 right-5 sm:right-8 w-11 h-11 rounded-full glass-card flex items-center justify-center text-amber-200"
      >
        {audioOn ? <Music size={17} /> : <VolumeX size={17} />}
      </button>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 text-amber-200/70"
      >
        <ChevronDown size={22} />
      </motion.div>
    </section>
  );
};
